import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { History, Search, Loader2, Sparkles, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const TIMELINE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zzuf7g8j_gemini_generated_video_48cfa062.mp4";

export function TimelineTool() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);

  const build = async (theme) => {
    const term = (theme ?? query).trim();
    if (!term || loading) return;
    setLoading(true); setOpenIdx(null); setData(null);
    try {
      const { data: res } = await api.get("/timeline", { params: { theme: term, lang: (i18n.language || "fr").slice(0, 2) } });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.timeline.error"));
    } finally { setLoading(false); }
  };

  const deepen = (ev) => {
    localStorage.setItem("coccon_pending_prompt", t("atelier.timeline.deepenPrompt", { title: ev.title, theme: data?.title || query, year: ev.year }));
    navigate("/atelier");
  };

  const presets = t("atelier.timeline.presets", { returnObjects: true }) || [];

  return (
    <section className="max-w-4xl mx-auto px-6 py-14 sm:py-24" data-testid="timeline-section">
      <div className="glass stats-wow rounded-[28px] border border-primary/15 px-5 sm:px-10 py-10 sm:py-14 relative overflow-hidden">
      <div className="relative z-10">
      <div className="text-center mb-9">
        <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
          <History size={14} /> {t("atelier.timeline.eyebrow")}
        </p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight mb-4">{t("atelier.timeline.title")}</h2>
        <p className="text-foreground/55 max-w-xl mx-auto leading-relaxed">{t("atelier.timeline.subtitle")}</p>
      </div>

      <div className="max-w-3xl mx-auto mb-10">
        <CinematicVideo fade noSound src={TIMELINE_VIDEO} testId="timeline-cinematic-video" />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); build(); }} className="max-w-xl mx-auto flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t("atelier.timeline.placeholder")}
            data-testid="timeline-input"
            className="w-full rounded-full bg-white/[0.03] border border-white/12 pl-11 pr-4 py-3.5 text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors"
          />
        </div>
        <button type="submit" disabled={loading} data-testid="timeline-build-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {t("atelier.timeline.go")}
        </button>
      </form>

      <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="timeline-presets">
        {presets.map((p) => (
          <button key={p} onClick={() => { setQuery(p); build(p); }} disabled={loading}
            className="text-sm px-4 py-1.5 rounded-full border border-white/10 text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-50">
            {p}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2" data-testid="timeline-loading">
          <Loader2 size={16} className="animate-spin" /> {t("atelier.timeline.loading")}
        </p>
      )}

      {data && data.events?.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} data-testid="timeline-result">
          <h3 className="font-display italic text-center text-2xl text-foreground/80 mb-9">{data.title}</h3>
          <div className="relative pl-2">
            {/* fil vertical */}
            <div className="absolute left-[7px] sm:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-primary/50 via-primary/20 to-transparent" aria-hidden />
            <div className="space-y-5">
              {data.events.map((ev, i) => {
                const open = openIdx === i;
                const left = i % 2 === 0;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.08 }}
                    data-testid={`timeline-event-${i}`}
                    className={`relative pl-8 sm:pl-0 sm:w-1/2 ${left ? "sm:pr-10" : "sm:ml-auto sm:pl-10"}`}
                  >
                    {/* point */}
                    <span className={`absolute top-4 w-3.5 h-3.5 rounded-full bg-primary shadow-[0_0_14px_hsl(32_90%_55%/0.8)] left-0 sm:left-auto ${left ? "sm:-right-[7px]" : "sm:-left-[7px]"}`} aria-hidden />
                    <button
                      onClick={() => setOpenIdx(open ? null : i)}
                      className="w-full text-left glass rounded-2xl p-5 border border-white/10 hover:border-primary/30 transition-colors"
                      data-testid={`timeline-toggle-${i}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <span className="text-primary font-display text-lg">{ev.year}</span>
                          <p className="font-display text-foreground/90 text-lg leading-tight">{ev.title}</p>
                        </div>
                        <ChevronDown size={18} className={`text-foreground/40 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                      </div>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }} className="overflow-hidden"
                          >
                            <p className="text-sm text-foreground/70 leading-relaxed mt-3">{ev.detail}</p>
                            <span
                              role="button" tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); deepen(ev); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deepen(ev); } }}
                              data-testid={`timeline-deepen-${i}`}
                              className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary/80 hover:text-primary transition-colors cursor-pointer"
                            >
                              <Sparkles size={13} /> {t("atelier.timeline.deepen")}
                            </span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
      </div>
      </div>
    </section>
  );
}
